import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, Check, ExternalLink, Play, ImageOff, X, ChevronLeft, ChevronRight,
  Apple, Github, Layers
} from 'lucide-react';
import ProjectIcon from '../components/ProjectIcon';
import ProjectCard from '../components/ProjectCard';
import { getProject, projects } from '../data/projects';
import './Projects.css';
import './ProjectDetail.css';

const tones = ['yellow', 'blue', 'green', 'purple'];

const ProjectDetail = () => {
  const { slug } = useParams();
  const project = getProject(slug);
  // The open image, tied to the project it belongs to so it resets on navigation
  const [viewer, setViewer] = useState(null);

  const images = project ? project.images : [];
  const lightbox = viewer && viewer.slug === slug ? viewer.i : null;
  const setLightbox = (i) => setViewer({ slug, i });
  const close = () => setViewer(null);
  const step = (d) => setViewer({ slug, i: (lightbox + d + images.length) % images.length });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  // Keyboard controls for the image viewer
  const isOpen = lightbox !== null;
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setViewer(null);
      if (e.key === 'ArrowRight') setViewer((v) => v && { ...v, i: (v.i + 1) % images.length });
      if (e.key === 'ArrowLeft') setViewer((v) => v && { ...v, i: (v.i - 1 + images.length) % images.length });
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [isOpen, images.length]);

  if (!project) {
    return (
      <div className="page-shell">
        <div className="container page-hero">
          <h1 className="page-title">Project not found</h1>
          <p className="page-subtitle">That project doesn't exist or has been moved.</p>
          <Link to="/products" className="btn btn-primary">Back to products</Link>
        </div>
      </div>
    );
  }

  const { links } = project;
  const index = projects.findIndex((p) => p.slug === project.slug);
  const tone = tones[index % tones.length];
  const others = projects.filter((p) => p.slug !== project.slug).slice(0, 3);
  const hasLinks = links.website || links.playStore || links.appStore || links.github;

  return (
    <>
      <title>{`${project.name} - Pidesk`}</title>
      <meta name="description" content={project.short} />

      <div className="detail-page">
        <div className="container detail-wrap">
          <Link to="/products" className="back-link"><ArrowLeft size={16} /> All products</Link>

          {/* Hero */}
          <header className={`detail-hero tone-${tone}`} style={project.cover ? { '--cover': `url(${project.cover})` } : undefined}>
            {project.cover && <div className="detail-cover" />}
            <div className="detail-hero-inner">
              <div className="detail-icon"><ProjectIcon name={project.icon} size={40} /></div>
              <div className="detail-hero-text">
                <div className="detail-pills">
                  <span className="dpill">{project.category}</span>
                  <span className={`dpill ${project.status === 'Live' ? 'live' : ''}`}>
                    {project.status === 'Live' && <i></i>}{project.status}
                  </span>
                </div>
                <h1 className="detail-name">{project.name}</h1>
                <p className="detail-tagline">{project.short}</p>

                <div className="detail-actions">
                  {links.website && (
                    <a className="btn btn-primary btn-lg" href={links.website} target="_blank" rel="noopener noreferrer">
                      <ExternalLink size={16} /> Open website
                    </a>
                  )}
                  {links.playStore && (
                    <a className="btn btn-primary btn-lg" href={links.playStore} target="_blank" rel="noopener noreferrer">
                      <Play size={16} /> Get it on Google Play
                    </a>
                  )}
                  {links.appStore && (
                    <a className="btn btn-primary btn-lg" href={links.appStore} target="_blank" rel="noopener noreferrer">
                      <Apple size={16} /> App Store
                    </a>
                  )}
                  {links.github && (
                    <a className="btn btn-secondary btn-lg" href={links.github} target="_blank" rel="noopener noreferrer">
                      <Github size={16} /> Source code
                    </a>
                  )}
                  <Link className={`btn ${hasLinks ? 'btn-secondary' : 'btn-primary'} btn-lg`} to="/contact">
                    Request a demo
                  </Link>
                </div>
              </div>
            </div>
          </header>

          <div className="detail-layout">
            {/* Main column */}
            <div className="detail-main">
              <section className="detail-section">
                <h2>About this project</h2>
                <p className="detail-about">{project.description}</p>
              </section>

              <section className="detail-section">
                <h2>Screenshots</h2>
                {images.length > 0 ? (
                  <div className="shots">
                    {images.map((src, i) => (
                      <button type="button" key={src} className="shot" onClick={() => setLightbox(i)} aria-label={`View screenshot ${i + 1}`}>
                        <img src={src} alt={`${project.name} screenshot ${i + 1}`} loading="lazy" />
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="shots-empty">
                    <ImageOff size={22} />
                    <span>Screenshots coming soon</span>
                  </div>
                )}
              </section>

              <section className="detail-section">
                <h2>Key features</h2>
                <ul className="feature-list">
                  {project.features.map((f) => (
                    <li key={f}><span className="feature-check"><Check size={14} /></span> {f}</li>
                  ))}
                </ul>
              </section>
            </div>

            {/* Sidebar */}
            <aside className="detail-side">
              <div className="side-card">
                <h3>Project info</h3>
                <dl className="side-info">
                  <div><dt>Category</dt><dd>{project.category}</dd></div>
                  <div><dt>Built for</dt><dd>{project.client}</dd></div>
                  <div><dt>Status</dt><dd>{project.status}</dd></div>
                  <div><dt>Developer</dt><dd>Pidesk</dd></div>
                </dl>
                <h4><Layers size={14} /> Built with</h4>
                <div className="tech-tags">
                  {project.tech.map((t) => <span key={t}>{t}</span>)}
                </div>
              </div>

              <div className="side-cta">
                <h3>Want something like this?</h3>
                <p>Tell us about your idea and we'll come back with a plan and estimate.</p>
                <Link to="/contact" className="btn btn-primary">Start a project <ArrowRight size={16} style={{ marginLeft: 8 }} /></Link>
              </div>
            </aside>
          </div>

          {others.length > 0 && (
            <section className="detail-section detail-more">
              <h2>More from Pidesk</h2>
              <div className="projects-grid">
                {others.map((p) => (
                  <ProjectCard key={p.slug} project={p} index={projects.findIndex((x) => x.slug === p.slug)} />
                ))}
              </div>
            </section>
          )}
        </div>
      </div>

      {/* Image viewer */}
      {lightbox !== null && (
        <div className="lightbox" role="dialog" aria-modal="true" onClick={close}>
          <button type="button" className="lb-btn lb-close" onClick={close} aria-label="Close"><X size={22} /></button>
          {images.length > 1 && (
            <>
              <button type="button" className="lb-btn lb-prev" onClick={(e) => { e.stopPropagation(); step(-1); }} aria-label="Previous"><ChevronLeft size={26} /></button>
              <button type="button" className="lb-btn lb-next" onClick={(e) => { e.stopPropagation(); step(1); }} aria-label="Next"><ChevronRight size={26} /></button>
            </>
          )}
          <img src={images[lightbox]} alt={`${project.name} screenshot ${lightbox + 1}`} onClick={(e) => e.stopPropagation()} />
          <span className="lb-count">{lightbox + 1} / {images.length}</span>
        </div>
      )}
    </>
  );
};

export default ProjectDetail;
